import {
	ReactElement,
	useState,
	useContext,
	useEffect,
	useCallback,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { PAGE_ICONS } from '../_const';
import PageWithBreadcrumbs from '../components/layouts/NEPageWithBreadcrumbs';
import ViewLayout from '../components/layouts/ViewLayout';
import {
	checkIfEpicCareLinkSiteIsProcessed,
	submitAutomationEpicCareLinkUserCreation,
} from '../_api';
import { UserContext } from 'context/User/UserContext';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * EpicCare Link User - new non-employee creation automation workflow
 * @returns {ReactElement} Automated EpicCare Link User Non-Employee creation/account request with help panel, breadcrumb navigation
 */
const NonEmployeeCompletionForm = () => {
	const { msgID } = useParams();
	const history = useHistory();
	const [trackingNumber, setTrackingNumber] = useState('');
	const [message, setMessage] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [isProcessed, setIsProcessed] = useState(false);
	const [orgNotFound, setOrgNotFound] = useState(false);
	const [timerCount, setTimerCount] = useState(6);

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);

	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const approveRequest = useCallback(
		async msgID => {
			// Implementation for approving the request
			var resp = await submitAutomationEpicCareLinkUserCreation(
				msgID,
				user
			);

			var payload = resp?.data?.payload;
			if (payload?.successful) {
				setTrackingNumber(payload?.esoTrackingNumber);
			}
			if (
				payload?.Message?.toLowerCase().includes(
					'organization not found'
				)
			) {
				setOrgNotFound(true);
			}
			setMessage(payload?.Message);

			setIsLoading(false);
		},
		[user]
	);
	const checkAndApprove = useCallback(() => {
		checkIfEpicCareLinkSiteIsProcessed(msgID).then(response => {
			const payload = response.data.payload;
			if (payload) {
				setIsProcessed(true);
				setIsLoading(false);
			} else {
				approveRequest(msgID);
			}
		});
	}, [msgID, approveRequest]);

	useEffect(() => {
		checkAndApprove();
	}, [checkAndApprove]);

	useEffect(() => {
		if (orgNotFound && timerCount > 0) {
			const timer = setTimeout(() => {
				setTimerCount(timerCount - 1);
			}, 1000);
			return () => clearTimeout(timer);
		} else if (orgNotFound && timerCount === 0) {
			history.push(
				`/Utilities/ThirdPartyOrganization/Dashboard?ACTION=ADD_LINK&MQ=${msgID}&TN=${trackingNumber}`
			);
		}
	}, [orgNotFound, timerCount, history, msgID, trackingNumber]);
	return (
		<PageWithBreadcrumbs
			currentPageLabel={'New EpicCare Link Auto Account creation'}
			CurrentPageIcon={PAGE_ICONS.IDENTITY}
		>
			<ViewLayout
				title={'New EpicCare Link Auto Account creation'}
				help={{
					title: 'Create New Non-Employee: EpicCare Link User',
					contents: (
						<Stack direction={'column'} spacing={2}>
							<Typography>
								This workflow is designed for EpicCare Link site
								administrators who wish to register themselves
								for a new site and for sponsors to automatically
								create them in IMS.
							</Typography>
						</Stack>
					),
				}}
			>
				<Stack
					direction='row'
					spacing={2}
					sx={{ padding: 5 }}
					justifyContent='center'
				>
					{isLoading ? (
						<CircularProgress />
					) : isProcessed ? (
						<h3>This request already been processed</h3>
					) : (
						<>
							{orgNotFound ? (
								<div style={{ textAlign: 'center' }}>
									<h4>{message}</h4>
									<h4>
										Redirecting to 3rd party organization
										page in {timerCount} seconds...
									</h4>
								</div>
							) : (
								<>
									{message && <h4>{message}</h4>}
									{!message && trackingNumber && (
										<h3>
											Application request successfully
											created. Tracking Number:{' '}
											{trackingNumber}
										</h3>
									)}
								</>
							)}
						</>
					)}
				</Stack>
			</ViewLayout>
		</PageWithBreadcrumbs>
	);
};

export default NonEmployeeCompletionForm;
